<template>
    <div>
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-6">
            <div class="bg-white shadow-md rounded-lg p-6 border border-gray-200 w-full mx-auto">
                <div class="flex justify-between items-center mb-6 border-b pb-4">
                    <h1 class="text-2xl font-bold text-gray-800">Simulazione Costificazione Di.Ba.</h1>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 items-end">
                    <div class="relative flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">Codice Prodotto</label>
                        <div class="flex">
                            <input
                                type="text"
                                v-model="filters.item"
                                placeholder="Codice Di.Ba...."
                                class="block min-w-0 w-full rounded-l-md border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border"
                            >
                            <button
                                type="button"
                                @click="apriSelettoreArticolo"
                                class="rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-600 hover:bg-gray-200 focus:border-blue-500 focus:ring-blue-500"
                                title="Seleziona articolo"
                                aria-label="Seleziona articolo"
                            >
                                <i class="fa fa-search"></i>
                            </button>
                        </div>
                        <span v-if="articoloSelezionato && filters.item === articoloSelezionato.Item" class="absolute left-0 top-full mt-1 text-xs text-gray-500">{{ articoloSelezionato.Description }}</span>
                    </div>

                    <div class="flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">Quantità da produrre (Kg)</label>
                        <input
                            type="number"
                            step="0.0001"
                            v-model.number="filters.quantita"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                        >
                    </div>

                    <div class="flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">Fonti costo da considerare</label>
                        <div class="flex flex-wrap items-center gap-x-4 gap-y-2 p-2">
                            <label v-for="fonte in tipiFonteOpzioni" :key="fonte.valore" class="inline-flex items-center gap-1 text-sm">
                                <input type="checkbox" :value="fonte.valore" v-model="filters.tipiFonte">
                                {{ fonte.label }}
                            </label>
                        </div>
                    </div>

                    <div class="flex flex-col">
                        <button
                            @click="cercaProposte"
                            :disabled="loading || !filters.item"
                            class="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold rounded-md p-2 text-sm"
                        >
                            {{ loading ? 'Ricerca in corso...' : 'Simula' }}
                        </button>
                    </div>
                </div>

                <div v-if="risultato && risultato.errore" class="p-4 text-center text-red-600 bg-red-50 rounded-md border border-red-200">
                    {{ risultato.errore }}
                </div>
                <div v-else-if="risultato && !risultato.trovata" class="p-4 text-center text-red-600 bg-red-50 rounded-md border border-red-200">
                    Distinta base non trovata per il codice indicato.
                </div>

                <div v-else-if="risultato && risultato.trovata">
                    <div v-if="articoloSemilavorato" class="mb-4 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-sm font-semibold">
                        Attenzione: l'articolo selezionato è un semilavorato.
                    </div>
                    <div class="mb-4 flex items-center justify-between">
                        <div class="text-sm text-gray-600">
                            Distinta: <span class="font-semibold font-mono text-sm">{{ risultato.item }}</span>
                            &nbsp;-&nbsp; Quantità richiesta: <span class="font-semibold">{{ risultato.quantita }} {{ risultato.uom }}</span>
                            &nbsp;- da fonti: <span class="font-semibold">{{ fontiCostoLabel }}</span>
                        </div>
                        <button
                            @click="calcolaTotale"
                            :disabled="loadingTotale || !haSelezioni"
                            class="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-semibold rounded-md px-4 py-2 text-sm"
                        >
                            {{ loadingTotale ? 'Calcolo in corso...' : 'Calcola totale simulazione' }}
                        </button>
                    </div>

                    <div class="mb-4 flex items-center justify-between flex-wrap gap-3">
                        <div v-if="fornitoriAutoSelezionabili.length" class="p-3 bg-blue-50 border border-blue-200 rounded-md flex-1">
                            <div class="text-sm font-semibold text-blue-800 mb-2">Selezione rapida per fornitore</div>
                            <div class="flex items-center gap-2">
                                <select
                                    v-model="fornitoreSelezionatoRapido"
                                    class="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                                >
                                    <option value="" disabled>Seleziona fornitore...</option>
                                    <option v-for="f in fornitoriAutoSelezionabili" :key="f.fornitore" :value="f.fornitore">
                                        {{ f.nome }}
                                    </option>
                                </select>
                                <button
                                    @click="selezionaFornitore(fornitoreSelezionatoRapido)"
                                    :disabled="!fornitoreSelezionatoRapido"
                                    class="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-xs font-semibold rounded-md px-3 py-2"
                                >
                                    Applica al minor costo <span v-if="risultato.tipiFonte.includes('magazzino')">(+ magazzino)</span>
                                </button>
                            </div>
                        </div>

                        <label class="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                            <input type="checkbox" v-model="soloValoriMinori">
                            Mostra solo valori minori (per fornitore)
                        </label>
                    </div>

                    <div v-for="componente in risultato.componenti" :key="componente.Component" class="mb-4 border border-gray-200 rounded-md overflow-hidden">
                        <button
                            type="button"
                            @click="toggleComponente(componente.Component)"
                            class="w-full bg-gray-50 px-4 py-2 flex items-center justify-between text-left"
                        >
                            <div class="flex items-center gap-2">
                                <i
                                    :class="componentiEspansi[componente.Component] ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"
                                    class="text-gray-500 text-xs"
                                ></i>
                                <span class="font-semibold font-mono text-sm">{{ componente.Component }}</span>
                                <span class="text-gray-500 ml-2 text-sm">{{ componente.Description }}</span>
                                <span v-if="!componentiEspansi[componente.Component] && selezioni[componente.Component]" class="text-blue-600 ml-2 text-sm">
                                    Selezionato <span class="font-mono text-xs">{{ selezioni[componente.Component].Item }}</span> - {{ selezioni[componente.Component].DocNo }} di {{ selezioni[componente.Component].SupplierName || selezioni[componente.Component].Supplier }}
                                </span>
                            </div>
                            <div class="flex items-center gap-3 text-sm">
                                <span>Qty Di.Ba.: <span class="font-semibold">{{ componente.QtyOriginale }}</span></span>
                                <span
                                    class="px-2 py-0.5 rounded text-xs font-semibold"
                                    :class="disponibilita(componente).class"
                                >
                                    {{ disponibilita(componente).label }}
                                </span>
                            </div>
                        </button>

                        <div v-if="componentiEspansi[componente.Component]">
                            <div v-if="!gruppiArticolo(componente).length" class="p-3 text-center text-gray-400 text-sm">
                                Nessuna proposta trovata per questo componente
                            </div>
                            <div
                                v-for="gruppo in gruppiArticolo(componente)"
                                :key="gruppo.articolo"
                                class="border-t border-gray-200"
                            >
                                <button
                                    type="button"
                                    @click="toggleArticolo(componente, gruppo.articolo)"
                                    class="w-full bg-blue-100 px-4 py-2 flex items-center justify-between text-left"
                                    v-if="gruppiArticolo(componente).length > 1"
                                >
                                    <div class="flex items-center gap-2">
                                        <i
                                            :class="articoloEspanso(componente, gruppo.articolo) ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"
                                            class="text-blue-600 text-xs"
                                        ></i>
                                        <span class="font-semibold text-sm text-blue-900 font-mono">{{ gruppo.articolo }}</span>
                                        <i
                                            v-if="gruppo.articolo === componente.Component"
                                            class="fa fa-star text-yellow-500"
                                            title="Articolo presente in distinta base"
                                        ></i>
                                        <span class="text-blue-700 text-xs">Concentr. {{ gruppo.concentrazione }}</span>
                                        <span v-if="gruppo.proposte.includes(selezioni[componente.Component])" class="text-blue-900 font-semibold text-xs">
                                            Selezionato {{ selezioni[componente.Component].DocNo }} di {{ selezioni[componente.Component].SupplierName || selezioni[componente.Component].Supplier }}
                                        </span>
                                    </div>
                                    <span class="text-xs text-blue-700 font-semibold">Min: {{ gruppo.prezzoMinimo }}</span>
                                </button>

                                <table v-if="articoloEspanso(componente, gruppo.articolo)" class="w-full text-sm">
                                    <thead class="bg-gray-50 text-gray-600">
                                        <tr>
                                            <th class="text-left p-2">Tipo</th>
                                            <th class="text-left p-2">Documento/Lotto</th>
                                            <th class="text-left p-2">Fornitore/Deposito</th>
                                            <th class="text-right p-2">Qtà necessaria</th>
                                            <th class="text-right p-2">Valore Unitario (EUR)</th>
                                            <th class="text-right p-2"><span class="text-green-600">Sconto</span>/<span class="text-red-600">Dazio</span> (EUR)</th>
                                            <th class="text-right p-2">Prezzo riproporzionato</th>
                                            <th class="text-center p-2">Sel.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="(proposta, idx) in gruppo.proposte"
                                            :key="idx"
                                            class="border-t border-gray-100 hover:bg-gray-50"
                                        >
                                            <td class="p-2">{{ tipoFonteLabel(proposta) }}</td>
                                            <td class="p-2" :class="coloreDocumento(proposta)">{{ proposta.DocNo }}</td>
                                            <td class="p-2">
                                                {{ proposta.SupplierName || proposta.Supplier }}
                                                <span v-if="proposta.Tipo === 'magazzino'"> ({{ parseFloat(proposta.BookInv).toFixed(2) }})</span>
                                            </td>
                                            <td class="p-2 text-right">{{ proposta.QtyNecessaria }}</td>
                                            <td class="p-2 text-right">{{ proposta.UnitValueEur }}</td>
                                            <td
                                                class="p-2 text-right font-bold"
                                                :class="proposta.ScontoDazioEur > 0 ? 'text-green-600' : (proposta.ScontoDazioEur < 0 ? 'text-red-600' : '')"
                                            >
                                                {{ Math.abs(proposta.ScontoDazioEur) }}
                                            </td>
                                            <td class="p-2 text-right font-semibold">{{ proposta.PrezzoRiproporzionato }}</td>
                                            <td class="p-2 text-center">
                                                <input
                                                    type="radio"
                                                    :name="'sel-' + componente.Component"
                                                    @change="selezionaProposta(componente.Component, proposta)"
                                                    :checked="selezioni[componente.Component] === proposta"
                                                >
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="totale" class="mt-6 border-t pt-4">
                    <div v-if="!totale.valido" class="p-4 text-center text-red-600 bg-red-50 rounded-md border border-red-200">
                        {{ totale.motivoScarto }}
                    </div>
                    <div v-else>
                        <table class="w-full text-sm mb-4">
                            <thead class="bg-gray-100 text-gray-600">
                                <tr>
                                    <th class="text-left p-2">Componente</th>
                                    <th class="text-left p-2">Utilizzato</th>
                                    <th class="text-right p-2">Qtà Originale</th>
                                    <th class="text-right p-2">Qtà Ricalcolata</th>
                                    <th class="text-right p-2">Prezzo Unitario</th>
                                    <th class="text-right p-2">Costo Materiale</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="riga in totale.componenti" :key="riga.Component" class="border-t border-gray-100">
                                    <td class="p-2 font-mono text-sm">{{ riga.Component }}</td>
                                    <td class="p-2 font-mono text-sm">{{ riga.ItemUtilizzato }}</td>
                                    <td class="p-2 text-right">{{ riga.QtyOriginale }}</td>
                                    <td class="p-2 text-right font-semibold">{{ riga.QtyRicalcolata }}</td>
                                    <td class="p-2 text-right">{{ riga.PrezzoUnitario }}</td>
                                    <td class="p-2 text-right">{{ riga.CostoMateriale }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="flex justify-end gap-8 text-lg">
                            <div>Costo Totale: <span class="font-bold">{{ totale.costoTotale }} EUR</span></div>
                            <div>Costo Unitario a Kg: <span class="font-bold">{{ totale.costoUnitarioKg }} EUR/Kg</span></div>
                        </div>

                        <div class="mt-6 border-t pt-4">
                            <h2 class="text-lg font-bold text-gray-800 mb-4">Studio Marginalità</h2>

                            <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 items-end">
                                <div class="flex flex-col">
                                    <label class="text-sm font-semibold text-gray-700 mb-1">Modalità</label>
                                    <select
                                        v-model="marginalita.modalita"
                                        class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                                    >
                                        <option value="cliente">Cliente</option>
                                        <option value="nazione">Nazione</option>
                                    </select>
                                </div>

                                <div v-if="marginalita.modalita === 'cliente'" class="flex flex-col md:col-span-2">
                                    <label class="text-sm font-semibold text-gray-700 mb-1">Cliente</label>
                                    <v-select
                                        :options="foundClients"
                                        :filterable="false"
                                        v-model="marginalita.cliente"
                                        label="name"
                                        @search="searchClients"
                                        placeholder="Cerca cliente..."
                                        class="w-full"
                                    >
                                        <template #no-options="{ search, searching }">
                                            <div v-if="searching">Ricerca in corso...</div>
                                            <div v-else-if="!search">Inizia a digitare per cercare un cliente...</div>
                                        </template>
                                    </v-select>
                                </div>

                                <div v-else class="flex flex-col md:col-span-2">
                                    <label class="text-sm font-semibold text-gray-700 mb-1">Nazione</label>
                                    <v-select
                                        :options="nazioni"
                                        v-model="marginalita.nazione"
                                        label="Description"
                                        placeholder="Seleziona nazione..."
                                        class="w-full"
                                    ></v-select>
                                </div>

                                <div class="flex flex-col">
                                    <button
                                        @click="cercaMarginalita"
                                        :disabled="loadingMarginalita || (marginalita.modalita === 'cliente' ? !marginalita.cliente : !marginalita.nazione)"
                                        class="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold rounded-md p-2 text-sm"
                                    >
                                        {{ loadingMarginalita ? 'Ricerca in corso...' : 'Cerca vendite' }}
                                    </button>
                                </div>
                            </div>

                            <div v-if="righeMarginalita !== null">
                                <div v-if="!righeMarginalita.length" class="p-3 text-center text-gray-400 text-sm">
                                    Nessuna vendita trovata per l'articolo indicato.
                                </div>
                                <table v-else class="w-full text-sm">
                                    <thead class="bg-gray-100 text-gray-600">
                                        <tr>
                                            <th class="text-left p-2">Articolo</th>
                                            <th class="text-left p-2">Cliente</th>
                                            <th class="text-right p-2">Valore Unitario (EUR)</th>
                                            <th class="text-right p-2"><span class="text-green-600">Sconto</span>/<span class="text-red-600">Maggiorazione</span> (EUR)</th>
                                            <th class="text-right p-2">Prezzo (EUR)</th>
                                            <th class="text-left p-2">Documento</th>
                                            <th class="text-left p-2">Data Prezzo</th>
                                            <th class="text-right p-2">% Redditività</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(riga, idx) in righeMarginalita" :key="idx" class="border-t border-gray-100">
                                            <td class="p-2 font-mono text-sm">{{ riga.Item }}</td>
                                            <td class="p-2">{{ riga.CompanyName }}</td>
                                            <td class="p-2 text-right">{{ riga.UnitValueEur }}</td>
                                            <td
                                                class="p-2 text-right font-bold"
                                                :class="riga.ScontoDazioEur > 0 ? 'text-green-600' : (riga.ScontoDazioEur < 0 ? 'text-red-600' : '')"
                                            >
                                                {{ Math.abs(riga.ScontoDazioEur) }}
                                            </td>
                                            <td class="p-2 text-right font-semibold">{{ riga.PrezzoEur }}</td>
                                            <td class="p-2">{{ riga.TipoLabel }} {{ riga.DocNo }}</td>
                                            <td class="p-2">{{ formatData(riga.Data) }}</td>
                                            <td class="p-2 text-right font-semibold">{{ calcolaRedditivita(riga.PrezzoEur) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="selettoreArticoloAperto" class="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div class="absolute inset-0 bg-black opacity-50" @click="chiudiSelettoreArticolo"></div>
            <div class="z-10 max-h-[85vh] w-full max-w-7xl overflow-y-auto rounded-lg bg-white shadow-xl">
                <div class="flex items-center justify-between border-b bg-gray-50 px-6 py-4">
                    <h2 class="text-xl font-bold text-gray-800">Seleziona articolo con distinta base</h2>
                    <button type="button" @click="chiudiSelettoreArticolo" class="text-gray-500 hover:text-gray-700" aria-label="Chiudi selezione articolo">
                        <i class="fa fa-times"></i>
                    </button>
                </div>
                <div class="p-6 pt-3">
                    <PageServerTable
                        :headers="articleTableHeaders"
                        :items="articleTableItems"
                        :items-per-page.sync="articleTableIpp"
                        :items-per-page-options="articleTableIppo"
                        :sort-by="articleTableSortBy"
                        :items-length="articleTableCount"
                        :reload-fn="caricaArticoli"
                        :item-value="'Item'"
                        density="compact"
                        :page="articleTablePage"
                        @click:row="selezionaArticolo"
                        hover
                        show-current-page
                    />
                </div>
            </div>
        </div>

        <ToastNotification
            :show="toast.show"
            :message="toast.message"
            :type="toast.type"
            @update:show="toast.show = $event"
        />
    </div>
</template>

<script>
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import PageServerTable from '@/Components/PageServerTable.vue';
import ToastNotification from '@/Components/ToastNotification.vue';
import axios from 'axios';

export default {
    components: {
        BreadCrumbs,
        PageServerTable,
        ToastNotification,
    },
    data() {
        return {
            loading: false,
            loadingTotale: false,
            filters: {
                item: '',
                quantita: 100,
                tipiFonte: ['magazzino', 'offerta', 'offerta-ps', 'offerta-kk', 'ordine'],
            },
            articoloSelezionato: null,
            selettoreArticoloAperto: false,
            articleTableHeaders: [
                { title: this.$t('go.fields.item_article'), value: 'Item', key: 'Item', filterable: true, filtervalue: '' },
                { title: this.$t('go.fields.description'), value: 'Description', key: 'Description', filterable: true, filtervalue: '' },
                { title: this.$t('go.fields.homogeneous_ctg'), value: 'HomogeneousCtg', key: 'HomogeneousCtg', filterable: true, filtervalue: '' },
                { title: this.$t('go.fields.commodity_ctg'), value: 'CommodityCtg', key: 'CommodityCtg', filterable: true, filtervalue: '' },
                { title: this.$t('go.fields.product_ctg'), value: 'ProductCtg', key: 'ProductCtg', filterable: true, filtervalue: '' },
                { title: this.$t('go.fields.item_type'), value: 'ItemType', key: 'ItemType', filterable: true, filtervalue: '' },
                { title: this.$t('go.fields.producer'), value: 'Producer', key: 'Producer', filterable: true, filtervalue: '' },
            ],
            articleTableItems: [],
            articleTableCount: 0,
            articleTableIpp: 10,
            articleTableIppo: [
                { value: 10, title: '10' },
                { value: 25, title: '25' },
                { value: 50, title: '50' },
                { value: 100, title: '100' },
            ],
            articleTableSortBy: [{ key: 'Item', order: 'asc' }],
            articleTablePage: 1,
            risultato: null,
            totale: null,
            selezioni: {},
            fornitoreSelezionatoRapido: '',
            soloValoriMinori: true,
            componentiEspansi: {},
            articoliEspansi: {},
            foundClients: [],
            searchContactsTimeout: null,
            nazioni: [],
            marginalita: {
                modalita: 'cliente',
                cliente: null,
                nazione: null,
            },
            righeMarginalita: null,
            loadingMarginalita: false,
            toast: {
                show: false,
                message: '',
                type: 'success',
            },
        };
    },
    computed: {
        navigation() {
            return [
                { name: 'Dashboard', href: route('dashboard') },
                { name: this.$t('modules.simulazione-costificazione'), href: route('modules.home', 'simulazione-costificazione') },
            ];
        },
        haSelezioni() {
            if (!this.risultato || !this.risultato.trovata) {
                return false;
            }
            return this.risultato.componenti.every((c) => this.selezioni[c.Component]);
        },
        articoloSemilavorato() {
            return Number(this.risultato?.nature) === this.MagoEnums.Item_Kind.Semifinished;
        },
        tipiFonteOpzioni() {
            return [
                { valore: 'magazzino', label: 'Magazzino' },
                { valore: 'offerta', label: 'Offerte standard' },
                { valore: 'offerta-ps', label: 'Offerte PS' },
                { valore: 'offerta-kk', label: 'Offerte KK' },
                { valore: 'ordine', label: 'Ordini' },
            ];
        },
        fontiCostoLabel() {
            return (this.risultato?.tipiFonte || [])
                .map((tipo) => this.tipiFonteOpzioni.find((fonte) => fonte.valore === tipo)?.label || tipo)
                .join(', ');
        },
        fornitoriAutoSelezionabili() {
            if (!this.risultato || !this.risultato.trovata) {
                return [];
            }

            const componenti = this.risultato.componenti;
            const includeMagazzino = this.risultato.tipiFonte.includes('magazzino');
            const fornitori = new Map();

            componenti.forEach((c) => {
                (c.Proposte || []).forEach((p) => {
                    if (p.Supplier && !fornitori.has(p.Supplier)) {
                        fornitori.set(p.Supplier, p.SupplierName || p.Supplier);
                    }
                });
            });

            const risultato = [];

            fornitori.forEach((nome, fornitore) => {
                const valido = componenti.every((c) => {
                    const proposte = c.Proposte || [];
                    const haFornitore = proposte.some((p) => p.Supplier === fornitore);
                    const haMagazzino = includeMagazzino && proposte.some((p) => p.Tipo === 'magazzino');
                    return haFornitore || haMagazzino;
                });

                if (valido) {
                    risultato.push({ fornitore, nome });
                }
            });

            return risultato;
        },
    },
    watch: {
        soloValoriMinori() {
            this.selezioni = {};
            this.totale = null;
        },
    },
    methods: {
        apriSelettoreArticolo() {
            this.selettoreArticoloAperto = true;
            this.articleTablePage = 1;
            this.caricaArticoli();
        },
        chiudiSelettoreArticolo() {
            this.selettoreArticoloAperto = false;
        },
        caricaArticoli(sortby = undefined, itemsperpage = undefined, page = undefined) {
            if (sortby) this.articleTableSortBy = sortby;
            if (itemsperpage) this.articleTableIpp = itemsperpage;
            if (page) this.articleTablePage = page;

            axios.post(route('modules.simulazione-costificazione.articoli-con-distinta'), {
                headers: this.articleTableHeaders,
                sortby: this.articleTableSortBy,
                itemsperpage: this.articleTableIpp,
                page: this.articleTablePage,
            }, {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
                .then(({ data }) => {
                    this.articleTableItems = data.articleTableItems;
                    this.articleTableCount = data.articleTableCount;
                    this.articleTableSortBy = data.articleTablesortby;
                })
                .catch(() => {
                    this.showToast('Errore durante il recupero degli articoli', 'error');
                });
        },
        selezionaArticolo(_event, row) {
            const articolo = row.item;
            this.filters.item = articolo.Item;
            this.articoloSelezionato = articolo;
            this.selettoreArticoloAperto = false;
            this.risultato = null;
            this.totale = null;
            this.selezioni = {};
            this.righeMarginalita = null;
        },
        showToast(message, type = 'success') {
            this.toast.message = message;
            this.toast.type = type;
            this.toast.show = true;
        },
        disponibilita(componente) {
            const proposte = componente.Proposte || [];
            const isDiretto = (p) => p.Item === componente.Component;
            const isMagazzino = (p) => p.Tipo === 'magazzino';
            const isOrdinabile = (p) => p.Tipo === 'offerta' || p.Tipo === 'ordine';

            if (proposte.some((p) => isMagazzino(p) && isDiretto(p))) {
                return { label: 'Disponibile a magazzino', class: 'bg-green-100 text-green-700' };
            }
            if (proposte.some((p) => isMagazzino(p) && !isDiretto(p))) {
                return { label: 'Disponibile alternativo a magazzino', class: 'bg-teal-100 text-teal-700' };
            }
            if (proposte.some((p) => isOrdinabile(p) && isDiretto(p))) {
                return { label: 'Ordinabile diretto', class: 'bg-yellow-100 text-yellow-700' };
            }
            if (proposte.some((p) => isOrdinabile(p) && !isDiretto(p))) {
                return { label: 'Ordinabile alternativo', class: 'bg-orange-100 text-orange-700' };
            }
            return { label: 'Non disponibile', class: 'bg-red-100 text-red-700' };
        },
        filtraProposte(proposte) {
            if (!this.soloValoriMinori) {
                return proposte;
            }

            const magazzino = proposte.filter((p) => p.Tipo === 'magazzino');
            const altre = proposte.filter((p) => p.Tipo !== 'magazzino');

            const minPerFornitoreETipo = new Map();

            altre.forEach((p) => {
                const fornitore = p.Supplier || p.SupplierName;
                const documento = String(p.DocNo || '').trim().toUpperCase();
                const categoria = 
                    p.Tipo === 'ordine' ? 'ordine'
                    : documento.endsWith('KK') ? 'offerta-kk'
                    : documento.endsWith('PS') ? 'offerta-ps' 
                    : 'offerta';
                const chiave = `${fornitore}|${categoria}`;
                const attuale = minPerFornitoreETipo.get(chiave);

                if (attuale === undefined || p.PrezzoRiproporzionato < attuale.PrezzoRiproporzionato) {
                    minPerFornitoreETipo.set(chiave, p);
                }
            });

            return [...magazzino, ...Array.from(minPerFornitoreETipo.values())];
        },
        gruppiArticolo(componente) {
            const proposteFiltrate = this.filtraProposte(componente.Proposte || []);
            const gruppi = new Map();

            proposteFiltrate.forEach((p) => {
                if (!gruppi.has(p.Item)) {
                    gruppi.set(p.Item, []);
                }
                gruppi.get(p.Item).push(p);
            });

            const risultato = [];

            gruppi.forEach((righe, articolo) => {
                const ordinate = [...righe].sort(
                    (a, b) => a.PrezzoRiproporzionato - b.PrezzoRiproporzionato
                );

                risultato.push({
                    articolo,
                    concentrazione: righe[0] ? righe[0].ConcentrazioneTrovata : null,
                    proposte: ordinate,
                    prezzoMinimo: ordinate.length ? ordinate[0].PrezzoRiproporzionato : Infinity,
                });
            });

            risultato.sort((a, b) => a.prezzoMinimo - b.prezzoMinimo);

            return risultato;
        },
        articoloEspanso(componente, articolo) {
            const chiave = componente.Component + '|' + articolo;
            return this.articoliEspansi[chiave] !== false;
        },
        toggleArticolo(componente, articolo) {
            const chiave = componente.Component + '|' + articolo;
            this.articoliEspansi[chiave] = !this.articoloEspanso(componente, articolo);
        },
        toggleComponente(component) {
            this.componentiEspansi[component] = !this.componentiEspansi[component];
        },
        tipoFonteLabel(proposta) {
            if (proposta.Tipo === 'offerta') {
                const documento = String(proposta.DocNo || '').trim().toUpperCase();
                if (documento.endsWith('KK')) return 'Offerta KK';
                if (documento.endsWith('PS')) return 'Offerta PS';
                return 'Offerta standard';
            }

            const map = {
                magazzino: 'Magazzino',
                ordine: 'Ordine',
            };
            return map[proposta.Tipo] ?? proposta.Tipo;
        },
        coloreDocumento(proposta) {
            if (proposta.Tipo !== 'offerta') {
                return '';
            }

            const documento = String(proposta.DocNo || '').trim().toUpperCase();
            if (documento.endsWith('KK')) {
                return 'bg-orange-100 text-orange-800 font-semibold';
            }
            if (documento.endsWith('PS')) {
                return 'bg-fuchsia-100 text-fuchsia-800 font-semibold';
            }

            return '';
        },
        selezionaProposta(component, proposta) {
            this.selezioni[component] = proposta;
            this.totale = null;
        },
        selezionaFornitore(fornitore) {
            const includeMagazzino = this.risultato.tipiFonte.includes('magazzino');

            this.risultato.componenti.forEach((c) => {
                const proposte = (c.Proposte || []).filter(
                    (p) => p.Supplier === fornitore || (includeMagazzino && p.Tipo === 'magazzino')
                );

                if (!proposte.length) {
                    return;
                }

                const migliore = proposte.reduce((min, p) =>
                    p.PrezzoRiproporzionato < min.PrezzoRiproporzionato ? p : min
                );

                this.selezioni[c.Component] = migliore;
            });
            this.totale = null;
        },
        cercaProposte() {
            if (!this.filters.item || !this.filters.tipiFonte.length) {
                this.showToast('Compilare codice prodotto e almeno una fonte costo', 'error');
                return;
            }

            this.loading = true;
            this.risultato = null;
            this.totale = null;
            this.selezioni = {};
            this.componentiEspansi = {};
            this.articoliEspansi = {};

            axios.post(route('modules.simulazione-costificazione.proposte-costo'), {
                item: this.filters.item,
                quantita: this.filters.quantita,
                tipiFonte: this.filters.tipiFonte,
            })
                .then(({ data }) => {
                    this.risultato = data;
                })
                .catch(() => {
                    this.showToast('Errore durante il recupero delle proposte di costo', 'error');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        calcolaTotale() {
            this.loadingTotale = true;
            this.totale = null;
            this.righeMarginalita = null;

            axios.post(route('modules.simulazione-costificazione.calcola-totale'), {
                item: this.filters.item,
                quantita: this.filters.quantita,
                selezioni: this.selezioni,
            })
                .then(({ data }) => {
                    this.totale = data;
                    if (data.valido && !this.nazioni.length) {
                        this.caricaNazioni();
                    }
                })
                .catch(() => {
                    this.showToast('Errore durante il calcolo del totale', 'error');
                })
                .finally(() => {
                    this.loadingTotale = false;
                });
        },
        caricaNazioni() {
            axios.get(route('modules.simulazione-costificazione.nazioni'))
                .then(({ data }) => {
                    this.nazioni = data;
                })
                .catch(() => {
                    this.showToast('Errore durante il recupero delle nazioni', 'error');
                });
        },
        searchClients(search, loading) {
            if (search.length >= 2) {
                loading(true);
                clearTimeout(this.searchContactsTimeout);
                this.searchContactsTimeout = setTimeout(() => {
                    axios
                        .get(route('modules.contratti.ricerca-clienti', search))
                        .then((response) => {
                            this.foundClients = response.data;
                            loading(false);
                        });
                }, 300);
            }
        },
        cercaMarginalita() {
            this.loadingMarginalita = true;
            this.righeMarginalita = null;

            axios.post(route('modules.simulazione-costificazione.marginalita'), {
                item: this.filters.item,
                customer: this.marginalita.modalita === 'cliente' ? this.marginalita.cliente?.id : null,
                countryCode: this.marginalita.modalita === 'nazione' ? this.marginalita.nazione?.ISOCountryCode : null,
            })
                .then(({ data }) => {
                    this.righeMarginalita = data;
                })
                .catch(() => {
                    this.showToast('Errore durante il recupero delle vendite', 'error');
                })
                .finally(() => {
                    this.loadingMarginalita = false;
                });
        },
        calcolaRedditivita(prezzoVendita) {
            if (!this.totale || !this.totale.valido || !prezzoVendita) {
                return '-';
            }
            const costo = this.totale.costoUnitarioKg;
            return (prezzoVendita/costo * 100 - 100).toFixed(2);
        },
        formatData(data) {
            if (!data) {
                return '-';
            }
            return new Date(data).toLocaleDateString('it-IT');
        },
    },
};
</script>
